import { useState, useEffect, useRef } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { Button } from '@wajeez-web/components/ui';
import { DownloadIcon, PlusIcon } from '@heroicons/react/solid';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import { Link, useNavigate } from 'react-router-dom';
import { convertMarkdownToHtml } from '@wajeez-web/core/utils';
import { useAppDispatch } from '@wajeez-web/stores/base';
import {
  AuthorsActions,
  createAuthorMultilingual,
  editAuthorMultilingual,
  getAuthorByIdMultilingual,
} from '@wajeez-web/stores/content';
import { useContentSelector } from '@wajeez-web/stores/content';
import { DeleteItemAlert } from '@wajeez-web/components/ui';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import { Content } from '@wajeez-web/core/api-base';
import { HandleTableData, Table } from '@wajeez-web/components/data-table';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';
import _ from 'lodash';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { DataTable, Dropdown, CheckBoxType } from '@wajeez-web/components/mui';

import AuthorsModalComponent from '../components/AuthorModalComponent';
import {
  DataTableWrapper,
  useCreateAuthorMultilingual,
  useEditAuthorMultilingual,
  useGetAuthorByIdMultilingual,
} from '@wajeez-web/components/helpers';

const AuthorsPage = () => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const exportDataRef = useRef<any>(null);

  const [isAuthorModalOpen, setIsAuthorModalOpen] = useState(false);
  const [selectedAuthorId, setSelectedAuthorId] = useState<number | undefined>(
    undefined
  );

  const {
    tablePreferances,
    setTablePreferences,
    getData,
    isTableReferencesLoading,
  } = HandleTableData(
    'authors',
    AuthorsActions.getAuthorsAsDataTableAsync,
    undefined,
    undefined,
    [{ property: 32, orderByDirection: 1 }]
  );

  const {
    dataTableData,
    datatableStatus,
    updatingStatus,
    exportData,
    exportDataStatus,
  } = useContentSelector((state) => state.authors);

  const authorByIdMultilingualState = useGetAuthorByIdMultilingual();
  const createAuthorMultilingualState = useCreateAuthorMultilingual();
  const editAuthorMultilingualState = useEditAuthorMultilingual();

  const { t } = useTranslation();

  const handleExport = async () => {
    if (dataTableData && tablePreferances) {
      const res = await dispatch(
        AuthorsActions.getAuthorsToBeExportedAsync({
          ...tablePreferances,
          pageNumber: 1,
          pageSize: dataTableData.totalCount,
        })
      );
      if (res.meta.requestStatus === 'fulfilled') {
        exportDataRef.current.link.click();
      }
    }
  };

  const extractExportedData = () => {
    const d = [];

    if (dataTableData?.totalCount && dataTableData?.totalCount > 0) {
      for (let i = 0; i < dataTableData?.totalCount; i++) {
        const row: { [key: string]: any } = {};

        exportData?.['columns']?.forEach((item: any) => {
          if (!item.isHidden) {
            row[item.header] = item.cells[i];
          }
        });

        d.push(row);
      }
    }
    return d;
  };

  const onEdit = (id: number) => {
    getAuthorByIdMultilingual(id);

    setSelectedAuthorId(Number(id));

    setIsAuthorModalOpen(true);
  };

  const onSuccess = () => {
    getData();

    setIsAuthorModalOpen(false);

    dispatch(
      CommonActions.setAlert({
        type: AlertType.SUCCESS,
        messages: [{ message: 'تمت العملية بنجاح', code: 200, field: '' }],
        for: 'page',
      })
    );
  };

  return (
    <>
      <CSVLink
        filename={'Authors.csv'}
        data={extractExportedData()}
        ref={exportDataRef}
        className="hidden"
      >
        Download me
      </CSVLink>
      <PageLayoutsContainer
        title={`Authors`}
        endSideComponent={[
          {
            element: (
              <Button
                title={'Add Author'}
                Icon={PlusIcon}
                onClick={() => {
                  setSelectedAuthorId(undefined);

                  setIsAuthorModalOpen(true);
                }}
                bg="primary"
                color="white"
              />
            ),
            permission: [AdminSystemPermissions.authors.add],
          },
        ]}
        breadCrumbsLinks={[{ title: '' }]}
      >
        <DataTableWrapper
          data={dataTableData as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          rowActions={(row) => {
            return [
              {
                title: 'Edit',
                endpoint: '',
                permission: AdminSystemPermissions.authors.edit,
                type: 'request',
                action: () => {
                  onEdit(Number(row[1]));
                },
              },
              {
                title: 'Content',
                permission: AdminSystemPermissions.authors.content,
                endpoint: ``,
                type: 'request',
                action: () => {
                  navigate(`${row[1]}/content`);
                },
              },
              {
                title: 'Delete',
                endpoint: '',
                permission: AdminSystemPermissions.authors.delete,
                type: 'request',
                action: () => {
                  DeleteItemAlert(
                    () =>
                      dispatch(
                        AuthorsActions.deleteAuthorAsync(
                          parseInt(row[1] as string)
                        )
                      ).then(() => getData()),
                    t('general.will delete') + row[4]
                  );
                },
              },
            ];
          }}
          isLoading={datatableStatus === 'loading'}
          extraActions={[
            {
              label: 'Export as CSV',
              onClick: () => {
                handleExport();
              },
              Icon: <DownloadIcon className="h-4 w-4" />,
            },
          ]}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
          columnsSelectors={(r) => {
            return {
              '4': (
                <div
                  className="text-[#38A7B4] font-medium cursor-pointer"
                  onClick={() => {
                    onEdit(Number(r[1]));
                  }}
                >
                  {r[4]}
                </div>
              ),
              '16': (
                <div
                  dangerouslySetInnerHTML={{
                    __html: convertMarkdownToHtml(r[16] as string)! || '',
                  }}
                />
              ),
            };
          }}
        />
      </PageLayoutsContainer>
      {isAuthorModalOpen && (
        <AuthorsModalComponent
          isOpen={isAuthorModalOpen}
          onClose={() => {
            setSelectedAuthorId(undefined);

            setIsAuthorModalOpen(false);
          }}
          onSubmit={async (data) => {
            if (data) {
              if (selectedAuthorId) {
                editAuthorMultilingual(
                  {
                    id: selectedAuthorId,
                    ...data,
                  },
                  onSuccess
                );
              } else {
                createAuthorMultilingual(data, onSuccess);
              }
            }
          }}
        />
      )}
    </>
  );
};

export default AuthorsPage;
